import React from 'react';
import {Tooltip as MuiTooltip, type TooltipProps as MuiTooltipProps} from '@mui/material';
import type {Theme as MuiTheme} from '@mui/material/styles';

import {useAriaAndDataProps} from '@famly/mf_ts-utils_data-aria-props';
import type {Theme} from '@famly/mf_theme';
import {Text} from '@famly/mf_data-display_text';

export type TooltipProps = {
    /**
     * The element that triggers the tooltip
     */
    children: MuiTooltipProps['children'];

    /**
     * The content of the tooltip
     */
    tooltipContent: MuiTooltipProps['title'];

    /**
     * Placement of the tooltip
     */
    placement?: MuiTooltipProps['placement'];

    /**
     * If true, interactions with the tooltip content will close the tooltip. Default is true.
     */
    disableInteractive?: MuiTooltipProps['disableInteractive'];

    /**
     * Whether the tooltip is disabled
     */
    isDisabled?: boolean;

    /**
     * If provided, controls whether the tooltip is visible (controlled mode).
     * When undefined, tooltip uses default hover/focus behavior.
     */
    open?: boolean;

    /**
     * Visual variant of the tooltip.
     * - 'default': neutral styling (default)
     * - 'inverted': dark background with light text
     * - 'info': informational styling
     * - 'warn': warning styling
     * - 'success': success styling
     * - 'error': error styling
     */
    variant?: 'default' | 'inverted' | 'info' | 'warn' | 'error';

    /**
     * Maximum width of the tooltip in pixels. Defaults to 245.
     */
    maxWidth?: number;

    /**
     * Pins the tooltip to the pointer instead of anchoring it to the child element.
     * Use when the child is too large for its centre to be near the pointer — e.g. a
     * full-height column or a whole-panel hover target.
     */
    followCursor?: MuiTooltipProps['followCursor'];

    /**
     * The number of milliseconds to wait before showing the tooltip.
     */
    enterDelay?: MuiTooltipProps['enterDelay'];

    /**
     * The number of milliseconds to wait before hiding the tooltip.
     */
    leaveDelay?: MuiTooltipProps['leaveDelay'];

    /**
     * Callback fired when the tooltip requests to be opened.
     */
    onOpen?: MuiTooltipProps['onOpen'];

    /**
     * Callback fired when the tooltip requests to be closed.
     */
    onClose?: MuiTooltipProps['onClose'];
} & DataProps;

type VariantColors = {
    borderColor: keyof Theme['colorPalette'];
    backgroundColor: keyof Theme['colorPalette'];
    color: keyof Theme['colorPalette'];
};

const variantStyles: Record<NonNullable<TooltipProps['variant']>, VariantColors> = {
    default: {backgroundColor: 'n50', borderColor: 'n100', color: 'n400'},
    inverted: {backgroundColor: 'n400', borderColor: 'n100', color: 'n50'},
    info: {backgroundColor: 'n25', borderColor: 'b400', color: 'n400'},
    warn: {backgroundColor: 'n25', borderColor: 'y400', color: 'n400'},
    error: {backgroundColor: 'n25', borderColor: 'r400', color: 'n400'},
};

export const Tooltip = (props: TooltipProps) => {
    const {
        children,
        tooltipContent,
        placement = 'top',
        isDisabled,
        open,
        variant = 'default',
        disableInteractive = true,
        maxWidth = 245,
        followCursor,
        enterDelay,
        leaveDelay,
        ...rest
    } = props;

    const {borderColor, backgroundColor, color} = variantStyles[variant];

    const dataAriaProps = useAriaAndDataProps(props);

    const popperSx = React.useMemo(
        () => ({
            '& .MuiTooltip-tooltip': {
                border: (theme: MuiTheme) => `1px solid ${theme.modernFamlyTheme.colorPalette[borderColor]}`,
                backgroundColor: (theme: MuiTheme) => theme.modernFamlyTheme.colorPalette[backgroundColor],
                color: (theme: MuiTheme) => theme.modernFamlyTheme.colorPalette[color],
                paddingX: 3,
                paddingY: 2,
                maxWidth,
            },
        }),
        [borderColor, backgroundColor, color, maxWidth],
    );

    return (
        <MuiTooltip
            title={
                typeof tooltipContent === 'string' ? <Text variant="subtitle">{tooltipContent}</Text> : tooltipContent
            }
            placement={placement}
            open={open}
            disableFocusListener={isDisabled}
            disableHoverListener={isDisabled}
            disableTouchListener={isDisabled}
            disableInteractive={disableInteractive}
            followCursor={followCursor}
            enterDelay={enterDelay}
            leaveDelay={leaveDelay}
            componentsProps={{
                popper: {
                    modifiers: [
                        {
                            name: 'offset',
                            options: {
                                offset: [0, -6],
                            },
                        },
                    ],
                    sx: popperSx,
                },
            }}
            {...rest}
            {...dataAriaProps}
        >
            {children}
        </MuiTooltip>
    );
};
